// Shared by SSR and client refreshes. Product identity, permissions, lot state and
// bidder state always come from the current request, never the product snapshot.
const set = require('lodash/set');
const toPath = require('lodash/toPath');
const {getPublicProductIdentifier} = require('./public-product-identity');
const reservedFields = new Set([
    '__proto__', 'constructor', 'prototype', 'id', 'uuid', 'productUuid', 'workspaceId',
    'productTemplate', 'productTemplateUuid', 'salesEvent', 'salesEventAssignment',
    'catalogueContext', 'viewerBid', 'bids', '_links', 'form', 'options', 'design', 'cardAction',
    'lotProductSnapshot', 'lotCardSnapshot', 'cardDetails', 'userDefinedFieldsets', 'snapshotCheckedLotId'
]);

const isFinalisedLot = product => ['sold', 'passedin'].includes(
    String(product?.salesEvent?.lotStatus || '').trim().toLowerCase()
);

const applyLotCardSnapshot = (product, snapshot = product?.lotCardSnapshot) => {
    if (!isFinalisedLot(product) || snapshot?.version !== 1
        || String(snapshot?.lotId) !== String(product?.salesEvent?.lotId)
        || String(snapshot?.productUuid || '').toLowerCase() !== String(getPublicProductIdentifier(product) || '').toLowerCase()
        || !snapshot?.values || !Array.isArray(snapshot.cardDetails)
        || !Array.isArray(snapshot.userDefinedFieldsets)) return product;
    const result = {...product};
    const ids = new Set(snapshot.cardDetails.map(detail => detail.field));
    const historicalFields = {};
    Object.entries(snapshot.values.fields || {}).forEach(([key, value]) => {
        const path = toPath(key);
        if (ids.has(key) && !reservedFields.has(path[0])
            && !path.some(part => ['__proto__', 'constructor', 'prototype'].includes(part))) {
            set(historicalFields, path, value);
            historicalFields[key] = value;
        }
    });
    Object.assign(result, historicalFields);
    ['title', 'thumbnail', 'images'].forEach(key => { result[key] = snapshot.values[key]; });
    // Current editor media metadata must not override saved image references.
    result.imagesState = undefined;
    result.cardDetails = snapshot.cardDetails;
    result.userDefinedFieldsets = snapshot.userDefinedFieldsets;
    result.lotCardSnapshot = snapshot;
    return result;
};

const applyLotProductSnapshot = (product, snapshot = product?.lotProductSnapshot) => {
    if (!isFinalisedLot(product) || snapshot?.version !== 1
        || String(snapshot?.lotId) !== String(product?.salesEvent?.lotId)
        || String(snapshot?.productUuid || '').toLowerCase() !== String(getPublicProductIdentifier(product) || '').toLowerCase()
        || !snapshot?.values || !Array.isArray(snapshot?.template?.userDefinedFieldsets)) return product;

    const result = {...product};
    const historicalFields = {};
    Object.entries(snapshot.values.fields || {}).forEach(([key, value]) => {
        const path = toPath(key);
        if (!reservedFields.has(path[0])
            && !path.some(part => ['__proto__', 'constructor', 'prototype'].includes(part))) {
            set(historicalFields, path, value);
            historicalFields[key] = value;
        }
    });
    Object.assign(result, historicalFields);
    ['title', 'description', 'images', 'thumbnail', 'disclaimers', 'sellingPriceDisclaimer'].forEach(key => {
        result[key] = snapshot.values[key];
    });
    result.productTemplate = {
        ...product.productTemplate,
        userDefinedFieldsets: snapshot.template.userDefinedFieldsets,
        pricingFields: snapshot.template.pricingFields || []
    };
    // A current SEO title/description must not contradict the historical content.
    result.seo = undefined;
    result.lotProductSnapshot = snapshot;
    return result;
};

const buildLotProductSnapshotPath = (product, catalogueNumber) => {
    const identifier = getPublicProductIdentifier(product);
    if (!isFinalisedLot(product) || !product?.salesEvent?.lotId || !identifier) return null;
    const catalogue = catalogueNumber ? `&catalogueNumber=${encodeURIComponent(catalogueNumber)}` : '';
    return `/api/v2/products/${encodeURIComponent(identifier)}/lot-product-snapshot?lotId=${encodeURIComponent(product.salesEvent.lotId)}${catalogue}`;
};

module.exports = {applyLotProductSnapshot, applyLotCardSnapshot, buildLotProductSnapshotPath, isFinalisedLot};
